import classNames from 'classnames';
import type { CSSProperties, FunctionComponent } from 'react';
import { useEffect } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';

import { Analytics } from '@trello/atlassian-analytics';
import { ErrorBoundary } from '@trello/error-boundaries';
import { sendErrorEvent } from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import type { Preview } from '@trello/image-previews';
import { smallestPreviewBiggerThan } from '@trello/image-previews';
import { token } from '@trello/theme';
import { UnsplashTracker } from '@trello/unsplash';

import { BoardTemplateBadge } from 'app/src/components/BoardTile';
import { BoardTileLarge } from 'app/src/components/BoardTile/BoardTileLarge';
import { NewBoardTileLarge } from 'app/src/components/BoardTile/NewBoardTileLarge';
import { Null } from 'app/src/components/Null';
import { useTemplateTileQuery } from './TemplateTileQuery.generated';

import styles from './TemplateTile.less';

export interface TemplateTileProps {
  boardId: string;
  isModernized?: boolean;
  onTemplateClicked: (boardId: string) => void;
}

export const getBackgroundStyle = ({
  backgroundUrl,
  backgroundColor,
  preview,
}: {
  backgroundUrl?: string;
  backgroundColor?: string;
  preview?: Preview;
}) => {
  const css: CSSProperties = {};
  if (backgroundUrl) {
    css.backgroundImage = `url('${preview?.url || backgroundUrl}')`;
    css.backgroundSize = 'cover';
    css.backgroundPosition = 'center';
  } else {
    css.backgroundColor =
      backgroundColor || token('color.background.brand.bold', '#0C66E4');
  }
  return css;
};

export const TemplateTile: FunctionComponent<TemplateTileProps> = ({
  boardId,
  isModernized,
  onTemplateClicked,
}) => {
  const { data, loading, error } = useTemplateTileQuery({
    variables: {
      boardId,
    },
    waitOn: ['None'],
  });

  const backgroundUrl = data?.board?.prefs?.backgroundImage;

  const { value: isRedesignEnabled } = useFeatureGate(
    'trello_personal_productivity_release',
  );

  useEffect(() => {
    if (backgroundUrl) {
      UnsplashTracker.trackOncePerInterval(backgroundUrl);
    }
  }, [backgroundUrl]);

  useEffect(() => {
    if (error) {
      sendErrorEvent(error, {
        tags: {
          ownershipArea: 'trello-web-eng',
          feature: 'Template Picker',
        },
        extraData: {
          component: 'TemplateTile',
        },
      });
    }
  }, [error]);

  useEffect(() => {
    if (!loading && !data?.board) {
      // network failures and non-200 responses
      // return `null` for data.board in our graphql
      Analytics.sendOperationalEvent({
        action: 'errored',
        actionSubject: 'fetchTemplate',
        source: 'templatePickerSection',
        containers: {
          board: {
            id: boardId,
          },
        },
      });
    }
  }, [loading, data?.board, boardId]);

  if (error || (!loading && !data?.board) || loading) {
    return null;
  }

  const title = data?.board?.name;
  const url = data?.board?.url;
  const backgroundColor = data?.board?.prefs?.backgroundColor;
  const backgroundImageScaled = data?.board?.prefs?.backgroundImageScaled;

  const preview = smallestPreviewBiggerThan(backgroundImageScaled, 300, 150);

  const backgroundStyle = getBackgroundStyle({
    backgroundColor: backgroundColor || undefined,
    backgroundUrl: backgroundUrl || undefined,
    preview: preview || undefined,
  });

  if (isRedesignEnabled) {
    return (
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-web-eng',
          feature: 'Template Picker',
        }}
      >
        <NewBoardTileLarge
          boardId={boardId}
          onBoardClick={() => onTemplateClicked(boardId)}
          showStar={false}
          className={isModernized ? undefined : styles.newTemplateTile}
        />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-web-eng',
        feature: 'Template Picker',
      }}
      errorHandlerComponent={Null}
    >
      {isModernized ? (
        <BoardTileLarge
          boardId={boardId}
          onBoardClick={() => onTemplateClicked(boardId)}
        />
      ) : (
        <a
          href={url}
          onClick={() => onTemplateClicked(boardId)}
          className={classNames(styles.templateTile)}
          style={backgroundStyle}
        >
          <span className={styles.templateTileFade} />
          <div className={styles.templateTileDetails}>
            <BoardTemplateBadge />
            <LinesEllipsis
              text={title}
              maxLine="2"
              className={styles.templateTitle}
              component="h1"
            />
          </div>
        </a>
      )}
    </ErrorBoundary>
  );
};
